import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = { style: {"color":"var(--ion-color-dark)"} }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 1 }

import type { UserProfile } from '@/types'
import type { Group } from '@/types/Group'
import HeaderComponent from '@/components/HeaderComponent.vue'
import RefresherComponent from '@/components/RefresherComponent.vue'
import { useAppConfig } from '@/composables/app'
import { useAttendantGroups } from '@/composables/attendantGroup'
import { usePlayerGroups } from '@/composables/playerGroup'
import { useCurrentUserProfile } from '@/composables/userProfile'
import { DEFAULT_GROUP_CATEGORY_ID, DEFAULT_GROUP_ID, DEFAULT_USER_ROLE_VALUE, USER_ROLES } from '@/constants'
import { getGroup } from '@/utils/playerGroup'
import { confirmPopup, errorPopup, toastPopup } from '@/utils/popup'
import { updateUserProfile } from '@/utils/userProfile'
import {
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
} from '@ionic/vue'
import DOMPurify from 'dompurify'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'


export default /*@__PURE__*/_defineComponent({
  __name: 'OnboardingPage',
  setup(__props) {

const router = useRouter()

// Strip Erreur, Anonyme & Newbie from ROLES
const selectableRoles = Object.fromEntries(
  Object.entries(USER_ROLES).filter(
    ([, value]) => ![USER_ROLES.Erreur, USER_ROLES.Anonyme, USER_ROLES.Newbie].includes(value),
  ),
)

// reactive data

const name = ref('')
const selectedRole = ref(DEFAULT_USER_ROLE_VALUE)
const selectedgroupCategoryId = ref(DEFAULT_GROUP_CATEGORY_ID)
const selectedGroupId = ref(DEFAULT_GROUP_ID)
const nameError = ref(false)
const isUpdatingProfile = ref(false)

// composables & computed data

const currentUser = useCurrentUserProfile()
const appConfig = useAppConfig()
const {
  data: playerGroups,
  pending: isLoadingPlayerGroups,
  error: errorLoadingGroups,
} = usePlayerGroups(selectedgroupCategoryId)

const isParticipant = computed(() => selectedRole.value === USER_ROLES.Participant)
const isAttendant = computed(() => selectedRole.value >= USER_ROLES.Animateur)
const loadStaffGroups = computed(() => (selectedRole.value >= USER_ROLES.Organisateur ? 'only' : 'exclude'))
const {
  data: attendantGroups,
  pending: isLoadingAttendantGroups,
  error: errorLoadingAttendantGroups,
} = useAttendantGroups(isAttendant, loadStaffGroups, true, currentUser)

watch([errorLoadingGroups, errorLoadingAttendantGroups], (errors) => {
  if (errors[0]) {
    console.error('Error loading player groups:', errors[0])
  }
  if (errors[1]) {
    console.error('Error loading attendant groups:', errors[1])
  }
})

const canSubmit = computed(() => {
  if (!name.value) return false
  if (
    isParticipant.value
    && selectedgroupCategoryId.value !== DEFAULT_GROUP_CATEGORY_ID
    && selectedGroupId.value !== DEFAULT_GROUP_ID
  ) {
    return true
  }
  if (isAttendant.value && selectedGroupId.value !== DEFAULT_GROUP_ID) return true
  if (selectedRole.value >= USER_ROLES.Organisateur) return true
  return false
})

// methods

function handleRoleChange() {
  // Reset category, player group and attendant group values when changing the role
  selectedgroupCategoryId.value = DEFAULT_GROUP_CATEGORY_ID
  selectedGroupId.value = DEFAULT_GROUP_ID
  if (!name.value) nameError.value = true
}
function handleNameChange() {
  nameError.value = !name.value
}

function processForm(groupData: Group) {
  isUpdatingProfile.value = true
  let newProfile: Partial<UserProfile>
  if (selectedRole.value === USER_ROLES.Participant) {
    newProfile = {
      name: DOMPurify.sanitize(name.value),
      role: selectedRole.value,
      groupId: selectedGroupId.value,
      groupName: groupData.name,
      hasDoneOnboarding: true,
    }
  } else {
    newProfile = {
      name: DOMPurify.sanitize(name.value),
      requestedRole: selectedRole.value,
      requestedGroupId: selectedGroupId.value,
      requestedGroupName: groupData.name,
      hasDoneOnboarding: true,
    }
  }
  if (!currentUser.value) return errorPopup('currentUser not found', 'Impossible de mettre à jour le profil')
  updateUserProfile(currentUser.value.id, newProfile)
    .then(() => {
      toastPopup('Ton profil a été mis à jour')
      isUpdatingProfile.value = false
    })
    .catch((error: any) => {
      errorPopup(error.message, `Le profile n'a pas pu être mis à jour`)
      isUpdatingProfile.value = false
    })
    .finally(() => {
      router.replace({ name: 'home' })
    })
  console.log('Profile udpated', newProfile)
}

async function submitForm() {
  let message = ''
  let groupData: Group

  // Check if all required fields are filled
  if (!name.value) return errorPopup('Mentionne ton totem ou ton nom')
  if (selectedRole.value < USER_ROLES.Participant) return errorPopup('Choisis un role')
  if (!selectedGroupId.value) return errorPopup('Choisis une section')

  try {
    groupData = await getGroup(selectedGroupId.value)
  } catch (error: any) {
    return errorPopup(error.message, `Une erreur est survenue`)
  }
  switch (selectedRole.value) {
    case USER_ROLES.Participant:
      message = `Tu as choisi le role de participant.<br /><br />
      Cela signifie que tu participeras à la Baden Battle avec la section ${groupData.name}.`
      break
    case USER_ROLES.Animateur:
      message = `Tu as choisi le role d'animateur.<br /><br />
      Cela signifie qu'un•e des chefs de la section ${groupData.name} ou un•e organisateur/organisatrice de la Baden Battle devra
      <b>valider ta demande</b> avant que tu ne puisses utiliser l'app.`
      break
    case USER_ROLES.Chef:
      message = `Tu as choisi le role de chef.<br /><br />
      Cela signifie qu'un•e des chefs de la section ${groupData.name} ou un•e organisateur/organisatrice de la Baden Battle devra
      <b>valider ta demande</b> avant que tu ne puisses utiliser l'app.`
      break
    case USER_ROLES.Organisateur:
      message = `Tu as choisi le role d'organisateur de la Baden Battle.<br /><br />
      Cela signifie qu'une autre personne avec le rôle d'organisateur de la Baden Battle devra <b>valider ta demande</b> avant que tu ne puisses utiliser l'app.`
      break
    case USER_ROLES.Administrateur:
      message = `Tu as choisi le role d'administrateur de l'application.<br /><br />
      Cela signifie qu'une autre personne avec le rôle d'administrateur devra <b>valider ta demande</b> avant que tu ne puisses utiliser l'app.`
      break
  }
  const handler = () => processForm(groupData)
  return confirmPopup(message, handler, null, 'Continuer ?')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonPage), null, {
    default: _withCtx(() => [
      _createVNode(HeaderComponent, { "page-title": "Onboarding" }),
      _createVNode(_unref(IonContent), {
        fullscreen: true,
        class: "ion-padding"
      }, {
        default: _withCtx(() => [
          _createVNode(RefresherComponent),
          _createVNode(_unref(IonCard), null, {
            default: _withCtx(() => [
              (_unref(currentUser) && _unref(currentUser).rejectionReason)
                ? (_openBlock(), _createBlock(_unref(IonCardHeader), { key: 0 }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(IonCardTitle), null, {
                        default: _withCtx(() => _cache[5] || (_cache[5] = [
                          _createTextVNode("Outch !")
                        ])),
                        _: 1
                      }),
                      _createElementVNode("p", _hoisted_1, [
                        _cache[6] || (_cache[6] = _createTextVNode(" Ta demande d'accès a été refusée. ")),
                        _cache[7] || (_cache[7] = _createElementVNode("br", null, null, -1)),
                        _cache[8] || (_cache[8] = _createElementVNode("br", null, null, -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(currentUser).rejectionReason) + " ", 1),
                        _cache[9] || (_cache[9] = _createElementVNode("br", null, null, -1)),
                        _cache[10] || (_cache[10] = _createElementVNode("br", null, null, -1)),
                        _cache[11] || (_cache[11] = _createTextVNode(" Tu peux réessayer ci-dessous. "))
                      ])
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createBlock(_unref(IonCardHeader), { key: 1 }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(IonCardTitle), null, {
                        default: _withCtx(() => _cache[12] || (_cache[12] = [
                          _createTextVNode("Bienvenue !")
                        ])),
                        _: 1
                      }),
                      _cache[13] || (_cache[13] = _createElementVNode("p", null, "Avant d'aller plus loin, faisons connaissance.", -1))
                    ]),
                    _: 1
                  })),
              _createElementVNode("form", {
                onSubmit: _withModifiers(submitForm, ["prevent"]),
                onKeydown: _withKeys(submitForm, ["enter"])
              }, [
                _createVNode(_unref(IonList), { class: "ion-no-padding" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonItem), null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonLabel), {
                          position: "floating",
                          color: "primary"
                        }, {
                          default: _withCtx(() => _cache[14] || (_cache[14] = [
                            _createTextVNode(" Totem / Nom ")
                          ])),
                          _: 1
                        }),
                        _createVNode(_unref(IonInput), {
                          modelValue: name.value,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((name).value = $event)),
                          name: "name",
                          type: "text",
                          autocorrect: "off",
                          required: "",
                          onIonChange: handleNameChange
                        }, null, 8, ["modelValue"]),
                        (nameError.value)
                          ? (_openBlock(), _createBlock(_unref(IonNote), {
                              key: 0,
                              slot: "error"
                            }, {
                              default: _withCtx(() => _cache[15] || (_cache[15] = [
                                _createTextVNode(" Mentionne ton totem ou ton nom ")
                              ])),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(IonItem), null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(IonLabel), {
                          position: "floating",
                          color: "primary"
                        }, {
                          default: _withCtx(() => _cache[16] || (_cache[16] = [
                            _createTextVNode(" Quel sera ton role durant la Baden Battle ? ")
                          ])),
                          _: 1
                        }),
                        _createVNode(_unref(IonSelect), {
                          modelValue: selectedRole.value,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedRole).value = $event)),
                          required: "",
                          interface: "popover",
                          onIonChange: handleRoleChange
                        }, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(selectableRoles), (value, roleName) => {
                              return (_openBlock(), _createBlock(_unref(IonSelectOption), {
                                key: value,
                                value: value
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(roleName), 1)
                                ]),
                                _: 2
                              }, 1032, ["value"]))
                            }), 128))
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    (isParticipant.value)
                      ? (_openBlock(), _createBlock(_unref(IonItem), { key: 0 }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(IonLabel), {
                              position: "floating",
                              color: "primary"
                            }, {
                              default: _withCtx(() => _cache[17] || (_cache[17] = [
                                _createTextVNode(" Type de section ")
                              ])),
                              _: 1
                            }),
                            _createVNode(_unref(IonSelect), {
                              modelValue: selectedgroupCategoryId.value,
                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedgroupCategoryId).value = $event)),
                              interface: "popover",
                              required: ""
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(appConfig)?.groupCategories, (groupCategory, id) => {
                                  return (_openBlock(), _createBlock(_unref(IonSelectOption), {
                                    key: id,
                                    value: id
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(groupCategory.name), 1)
                                    ]),
                                    _: 2
                                  }, 1032, ["value"]))
                                }), 128))
                              ]),
                              _: 1
                            }, 8, ["modelValue"])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (isParticipant.value && selectedgroupCategoryId.value)
                      ? (_openBlock(), _createBlock(_unref(IonItem), { key: 1 }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(IonLabel), {
                              position: "floating",
                              color: "primary"
                            }, {
                              default: _withCtx(() => _cache[18] || (_cache[18] = [
                                _createTextVNode(" Section ")
                              ])),
                              _: 1
                            }),
                            (_unref(isLoadingPlayerGroups))
                              ? (_openBlock(), _createBlock(_unref(IonSpinner), { key: 0 }))
                              : (_unref(errorLoadingGroups))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_2, " Erreur "))
                                : (_openBlock(), _createBlock(_unref(IonSelect), {
                                    key: 2,
                                    modelValue: selectedGroupId.value,
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedGroupId).value = $event)),
                                    interface: "popover",
                                    required: ""
                                  }, {
                                    default: _withCtx(() => [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(playerGroups), (playerGroup) => {
                                        return (_openBlock(), _createBlock(_unref(IonSelectOption), {
                                          key: playerGroup.id,
                                          value: playerGroup.id
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(playerGroup.name) + " (" + _toDisplayString(playerGroup.city) + ") ", 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["value"]))
                                      }), 128))
                                    ]),
                                    _: 1
                                  }, 8, ["modelValue"]))
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (isAttendant.value)
                      ? (_openBlock(), _createBlock(_unref(IonItem), { key: 2 }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(IonLabel), {
                              position: "floating",
                              color: "primary"
                            }, {
                              default: _withCtx(() => _cache[19] || (_cache[19] = [
                                _createTextVNode(" Section ")
                              ])),
                              _: 1
                            }),
                            (_unref(isLoadingAttendantGroups))
                              ? (_openBlock(), _createBlock(_unref(IonSpinner), { key: 0 }))
                              : (_unref(errorLoadingAttendantGroups))
                                ? (_openBlock(), _createElementBlock("div", _hoisted_3, " Erreur "))
                                : (_openBlock(), _createBlock(_unref(IonSelect), {
                                    key: 2,
                                    modelValue: selectedGroupId.value,
                                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedGroupId).value = $event)),
                                    interface: "popover",
                                    required: ""
                                  }, {
                                    default: _withCtx(() => [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(attendantGroups), (attendantgroup) => {
                                        return (_openBlock(), _createBlock(_unref(IonSelectOption), {
                                          key: attendantgroup.id,
                                          value: attendantgroup.id
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(attendantgroup.name) + " (" + _toDisplayString(attendantgroup.city) + ") ", 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["value"]))
                                      }), 128))
                                    ]),
                                    _: 1
                                  }, 8, ["modelValue"]))
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(IonButton), {
                  type: "submit",
                  expand: "block",
                  class: "ion-margin",
                  disabled: !canSubmit.value
                }, {
                  default: _withCtx(() => _cache[20] || (_cache[20] = [
                    _createTextVNode(" Continuer ")
                  ])),
                  _: 1
                }, 8, ["disabled"])
              ], 32)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})